import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Users, Search, Edit, Trash2, Key, Mail, Phone, User, Shield, 
  CheckCircle, XCircle, Loader2, History, AlertTriangle, Save,
  Eye, EyeOff, RefreshCw, Crown, UserCog, Clock, UserMinus, UserCheck,
  MoreVertical, CheckSquare, Square, Archive, Power, PowerOff
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// User Type Badge Colors
const userTypeBadges = {
  customer: { label: 'Customer', color: 'bg-blue-100 text-blue-800' },
  mechanic: { label: 'Provider', color: 'bg-green-100 text-green-800' },
  roadside_provider: { label: 'Roadside', color: 'bg-teal-100 text-teal-800' },
  admin: { label: 'Admin', color: 'bg-purple-100 text-purple-800' },
  owner: { label: 'Owner', color: 'bg-yellow-100 text-yellow-800' }
};

// Edit User Modal
function EditUserModal({ isOpen, onClose, user, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    new_password: '',
    user_type: '',
    is_active: true,
    notes: ''
  });
  const [loading, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        new_password: '',
        user_type: user.user_type || 'customer',
        is_active: user.is_active !== false,
        notes: user.admin_notes || ''
      });
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(user.user_id, formData);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="w-5 h-5 text-blue-600" />
            Edit Account
          </DialogTitle>
          <DialogDescription>
            Make changes to user account. All changes are logged.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
              placeholder="Enter full name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
              placeholder="Enter email"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
              placeholder="Enter phone number"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">New Password (leave blank to keep current)</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.new_password}
                onChange={(e) => setFormData(p => ({ ...p, new_password: e.target.value }))}
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Account Type</Label>
            <Select 
              value={formData.user_type} 
              onValueChange={(value) => setFormData(p => ({ ...p, user_type: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select account type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="customer">Customer</SelectItem>
                <SelectItem value="mechanic">Provider (Mechanic)</SelectItem>
                <SelectItem value="roadside_provider">Roadside Provider</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">Account Active</p>
              <p className="text-sm text-gray-500">Inactive accounts cannot log in</p>
            </div>
            <button
              type="button"
              onClick={() => setFormData(p => ({ ...p, is_active: !p.is_active }))}
              className={`w-12 h-6 rounded-full transition-colors ${formData.is_active ? 'bg-green-500' : 'bg-gray-300'}`}
            >
              <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${formData.is_active ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Admin Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
              placeholder="Internal notes about this account..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Deactivate User Modal
function DeactivateModal({ isOpen, onClose, user, onDeactivate }) {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDeactivate = async () => {
    if (!reason.trim()) {
      toast.error('Please provide a reason for deactivation');
      return;
    }
    setLoading(true);
    try {
      await onDeactivate(user.user_id, reason, notes);
      setReason('');
      setNotes('');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-600">
            <PowerOff className="w-5 h-5" />
            Deactivate Account
          </DialogTitle>
          <DialogDescription>
            Deactivating keeps the account on record. The user will not be able to log in.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <p className="font-medium text-orange-900">{user.name}</p>
            <p className="text-sm text-orange-700">{user.email}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason for Deactivation *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger>
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user_request">User Request</SelectItem>
                <SelectItem value="policy_violation">Policy Violation</SelectItem>
                <SelectItem value="fraud">Fraud/Suspicious Activity</SelectItem>
                <SelectItem value="inactivity">Prolonged Inactivity</SelectItem>
                <SelectItem value="duplicate">Duplicate Account</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="deact-notes">Additional Notes (Optional)</Label>
            <Textarea
              id="deact-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional details..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={handleDeactivate} disabled={loading || !reason}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PowerOff className="w-4 h-4 mr-2" />}
            Deactivate Account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Context Menu Component
function ContextMenu({ x, y, visible, onClose, selectedCount, onAction }) {
  if (!visible) return null;

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div 
        className="fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[200px]"
        style={{ left: x, top: y }}
        data-testid="context-menu"
      >
        <div className="px-3 py-2 border-b border-gray-100">
          <p className="text-sm font-medium text-gray-900">{selectedCount} accounts selected</p>
        </div>
        <button
          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 text-orange-600"
          onClick={() => onAction('deactivate')}
        >
          <PowerOff className="w-4 h-4" />
          Deactivate Selected
        </button>
        <button
          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 text-green-600"
          onClick={() => onAction('activate')}
        >
          <Power className="w-4 h-4" />
          Activate Selected
        </button>
        <div className="border-t border-gray-100 mt-1 pt-1">
          <button
            className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 flex items-center gap-2 text-red-600"
            onClick={() => onAction('delete')}
          >
            <Trash2 className="w-4 h-4" />
            Delete Selected
          </button>
        </div>
      </div>
    </>
  );
}

// User Row Component with Selection
function UserRow({ user, onEdit, onResetPassword, onDelete, onDeactivate, onReactivate, isOwner, isSelected, onSelect }) {
  const typeConfig = userTypeBadges[user.user_type] || userTypeBadges.customer;
  const isDeactivated = user.is_active === false;
  
  return (
    <div 
      className={`flex items-center justify-between p-3 sm:p-4 bg-white rounded-lg border transition-colors cursor-pointer ${
        isSelected ? 'border-blue-400 bg-blue-50' : 'border-gray-100 hover:border-gray-200'
      } ${isDeactivated ? 'opacity-60' : ''}`}
      data-testid={`user-row-${user.user_id}`}
      onClick={(e) => {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          onSelect(user.user_id);
        }
      }}
    >
      <div className="flex items-center gap-2 sm:gap-4">
        <div 
          className="flex items-center justify-center"
          onClick={(e) => { e.stopPropagation(); onSelect(user.user_id); }}
        >
          <Checkbox
            checked={isSelected}
            className="h-4 w-4"
            data-testid={`select-${user.user_id}`}
          />
        </div>
        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
          user.is_owner ? 'bg-yellow-100' : user.is_admin ? 'bg-purple-100' : 'bg-gray-100'
        }`}>
          {user.is_owner ? (
            <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600" />
          ) : user.is_admin ? (
            <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
          ) : (
            <User className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
          )}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-gray-900 text-sm sm:text-base truncate">{user.name}</p>
            <Badge className={`${typeConfig.color} text-xs`}>{typeConfig.label}</Badge>
            {isDeactivated && <Badge className="bg-red-100 text-red-800 text-xs">Inactive</Badge>}
          </div>
          <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 mt-0.5">
            <span className="flex items-center gap-1 truncate">
              <Mail className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{user.email}</span>
            </span>
            {user.phone && (
              <span className="hidden sm:flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {user.phone}
              </span>
            )}
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onEdit(user); }} className="h-8 w-8 p-0">
          <Edit className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onResetPassword(user); }} className="h-8 w-8 p-0">
          <Key className="w-4 h-4" />
        </Button>
        {isOwner && !user.is_owner && (
          <>
            {isDeactivated ? (
              <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-700 h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); onReactivate(user); }}>
                <Power className="w-4 h-4" />
              </Button>
            ) : (
              <Button variant="ghost" size="sm" className="text-orange-600 hover:text-orange-700 h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); onDeactivate(user); }}>
                <PowerOff className="w-4 h-4" />
              </Button>
            )}
            <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); onDelete(user); }}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// Deactivated Accounts Modal
function DeactivatedAccountsModal({ isOpen, onClose, getAuthHeaders }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchDeactivatedAccounts();
    }
  }, [isOpen]);

  const fetchDeactivatedAccounts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/accounts/deactivated/list`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setRecords(response.data?.records || []);
    } catch (error) {
      toast.error('Failed to fetch deactivated accounts');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive className="w-5 h-5 text-orange-600" />
            Deactivated Accounts History
          </DialogTitle>
          <DialogDescription>
            Complete record of all deactivated accounts
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-3 mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Archive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No deactivated accounts on record</p>
            </div>
          ) : (
            records.map((record) => (
              <div key={record.record_id} className="p-4 bg-gray-50 rounded-lg border">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-medium text-gray-900">{record.user_name}</p>
                    <p className="text-sm text-gray-600">{record.user_email}</p>
                  </div>
                  <Badge className="bg-orange-100 text-orange-800">{record.reason}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mt-3">
                  <div>
                    <span className="text-gray-500">Type:</span> {record.user_type}
                  </div>
                  <div>
                    <span className="text-gray-500">Deactivated:</span> {new Date(record.deactivated_at).toLocaleDateString()}
                  </div>
                  <div>
                    <span className="text-gray-500">By:</span> {record.deactivated_by_name || record.deactivated_by}
                  </div>
                  <div>
                    <span className="text-gray-500">Phone:</span> {record.phone || 'N/A'}
                  </div>
                </div>
                {record.notes && (
                  <div className="mt-2 p-2 bg-white rounded text-sm text-gray-600">
                    <span className="text-gray-500">Notes:</span> {record.notes}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Main Admin Account Management Component
export default function AdminAccountManagement() {
  const { user, getAuthHeaders } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showDeactivatedHistory, setShowDeactivatedHistory] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  
  // Multi-select state
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0 });
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async (search = '') => {
    setLoading(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await axios.get(`${API}/admin/accounts/all${params}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setUsers(response.data?.users || []);
      // Clear selection after refresh
      setSelectedUsers(new Set());
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchUsers(searchQuery);
  };

  const handleSaveUser = async (userId, data) => {
    try {
      const payload = {};
      if (data.name) payload.name = data.name;
      if (data.email) payload.email = data.email;
      if (data.phone) payload.phone = data.phone;
      if (data.new_password) payload.new_password = data.new_password;
      if (data.user_type) payload.user_type = data.user_type;
      if (data.is_active !== undefined) payload.is_active = data.is_active;
      if (data.notes) payload.notes = data.notes;

      await axios.put(`${API}/admin/accounts/${userId}`, payload, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Account updated successfully');
      fetchUsers(searchQuery);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update account');
      throw error;
    }
  };

  const handleResetPassword = async (targetUser) => {
    if (!window.confirm(`Reset password for ${targetUser.name}? A temporary password will be generated.`)) return;
    
    try {
      const response = await axios.post(`${API}/admin/accounts/reset-password/${targetUser.user_id}`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      toast.success(
        <div>
          <p className="font-semibold">Password Reset!</p>
          <p className="text-sm">Temporary: {response.data.temporary_password}</p>
        </div>,
        { duration: 10000 }
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      await axios.delete(`${API}/admin/accounts/${selectedUser.user_id}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Account deleted');
      setShowDeleteConfirm(false);
      setSelectedUser(null);
      // Stay on page and refresh list
      fetchUsers(searchQuery);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    }
  };

  const handleDeactivateUser = async (userId, reason, notes) => {
    try {
      await axios.post(`${API}/admin/accounts/${userId}/deactivate`, { reason, notes }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Account deactivated');
      fetchUsers(searchQuery);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate account');
      throw error;
    }
  };

  const handleReactivateUser = async (targetUser) => {
    if (!window.confirm(`Reactivate ${targetUser.name}'s account?`)) return;
    
    try {
      await axios.post(`${API}/admin/accounts/${targetUser.user_id}/reactivate`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Account reactivated');
      fetchUsers(searchQuery);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate account');
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/accounts/audit-logs`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setAuditLogs(response.data?.logs || []);
      setShowAuditLogs(true);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    }
  };

  // Multi-select handlers
  const toggleUserSelection = useCallback((userId) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  }, []);

  const selectAllUsers = () => {
    const selectableUsers = users.filter(u => !u.is_owner).map(u => u.user_id);
    setSelectedUsers(new Set(selectableUsers));
  };

  const clearSelection = () => {
    setSelectedUsers(new Set());
  };

  // Context menu handler
  const handleContextMenu = useCallback((e) => {
    if (selectedUsers.size > 0) {
      e.preventDefault();
      setContextMenu({
        visible: true,
        x: Math.min(e.clientX, window.innerWidth - 220),
        y: Math.min(e.clientY, window.innerHeight - 200)
      });
    }
  }, [selectedUsers.size]);

  const closeContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0 });
  };

  // Bulk action handler
  const handleBulkAction = async (action) => {
    closeContextMenu();
    
    if (selectedUsers.size === 0) {
      toast.error('No users selected');
      return;
    }

    const actionNames = {
      deactivate: 'deactivate',
      activate: 'activate',
      delete: 'permanently delete'
    };

    if (!window.confirm(`Are you sure you want to ${actionNames[action]} ${selectedUsers.size} account(s)?`)) {
      return;
    }

    setBulkActionLoading(true);
    try {
      const response = await axios.post(`${API}/admin/accounts/bulk-action`, {
        user_ids: Array.from(selectedUsers),
        action: action,
        reason: action === 'deactivate' ? 'Bulk deactivation by owner' : undefined
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      const results = response.data.results;
      if (results.success.length > 0) {
        toast.success(`Successfully ${action}d ${results.success.length} account(s)`);
      }
      if (results.failed.length > 0) {
        toast.error(`Failed to ${action} ${results.failed.length} account(s)`);
      }
      
      fetchUsers(searchQuery);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} accounts`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const isOwner = user?.is_owner;

  return (
    <div 
      className="space-y-4 sm:space-y-6" 
      data-testid="admin-account-management"
      onContextMenu={handleContextMenu}
    >
      {/* Context Menu */}
      <ContextMenu
        x={contextMenu.x}
        y={contextMenu.y}
        visible={contextMenu.visible}
        onClose={closeContextMenu}
        selectedCount={selectedUsers.size}
        onAction={handleBulkAction}
      />

      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <UserCog className="w-5 h-5 text-blue-600" />
                Account Management
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Manage all user accounts - Ctrl+Click or checkbox to multi-select, right-click for bulk actions
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {isOwner && (
                <Button variant="outline" size="sm" onClick={() => setShowDeactivatedHistory(true)} className="h-8 text-xs sm:text-sm">
                  <Archive className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Deactivated</span>
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={fetchAuditLogs} className="h-8 text-xs sm:text-sm">
                <History className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Audit Logs</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 flex-col sm:flex-row">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search by name, email, or phone..."
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSearch} size="sm" className="h-10">
                <Search className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Search</span>
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setSearchQuery(''); fetchUsers(); }} className="h-10">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selection Bar */}
      {selectedUsers.size > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-3 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <CheckSquare className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-900">{selectedUsers.size} selected</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={selectAllUsers} className="h-8 text-xs">
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={clearSelection} className="h-8 text-xs">
                Clear
              </Button>
              <div className="border-l border-blue-300 h-6 mx-1 hidden sm:block" />
              <Button 
                variant="outline" 
                size="sm" 
                className="h-8 text-xs text-orange-600 border-orange-200 hover:bg-orange-50"
                onClick={() => handleBulkAction('deactivate')}
                disabled={bulkActionLoading}
              >
                <PowerOff className="w-3 h-3 mr-1" />
                Deactivate
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="h-8 text-xs text-green-600 border-green-200 hover:bg-green-50"
                onClick={() => handleBulkAction('activate')}
                disabled={bulkActionLoading}
              >
                <Power className="w-3 h-3 mr-1" />
                Activate
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => handleBulkAction('delete')}
                disabled={bulkActionLoading}
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Delete
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Users List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base sm:text-lg flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Users className="w-5 h-5 text-gray-600" />
              All Accounts
            </span>
            <Badge variant="outline">{users.length} users</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 sm:space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No users found</p>
            </div>
          ) : (
            users.map((u) => (
              <UserRow
                key={u.user_id}
                user={u}
                onEdit={setEditingUser}
                onResetPassword={handleResetPassword}
                onDelete={(u) => { setSelectedUser(u); setShowDeleteConfirm(true); }}
                onDeactivate={(u) => { setSelectedUser(u); setShowDeactivateModal(true); }}
                onReactivate={handleReactivateUser}
                isOwner={isOwner}
                isSelected={selectedUsers.has(u.user_id)}
                onSelect={toggleUserSelection}
              />
            ))
          )}
        </CardContent>
      </Card>

      {/* Edit User Modal */}
      <EditUserModal
        isOpen={!!editingUser}
        onClose={() => setEditingUser(null)}
        user={editingUser}
        onSave={handleSaveUser}
      />

      {/* Deactivate Modal */}
      <DeactivateModal
        isOpen={showDeactivateModal}
        onClose={() => { setShowDeactivateModal(false); setSelectedUser(null); }}
        user={selectedUser}
        onDeactivate={handleDeactivateUser}
      />

      {/* Delete Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Account
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            Are you sure you want to delete <strong>{selectedUser?.name}</strong>'s account? 
            This will mark the account as deleted.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Consider using <span className="font-medium text-orange-600">Deactivate</span> instead to keep detailed records.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteUser}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivated Accounts History */}
      <DeactivatedAccountsModal
        isOpen={showDeactivatedHistory}
        onClose={() => setShowDeactivatedHistory(false)}
        getAuthHeaders={getAuthHeaders}
      />

      {/* Audit Logs Modal */}
      <Dialog open={showAuditLogs} onOpenChange={setShowAuditLogs}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-blue-600" />
              Recent Account Changes
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {auditLogs.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No audit logs found</p>
            ) : (
              auditLogs.map((log, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline">{log.action}</Badge>
                    <span className="text-xs text-gray-500">
                      {new Date(log.performed_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">
                    Target: {log.target_user_id}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    By: {log.performed_by}
                  </p>
                  {log.changes && (
                    <pre className="text-xs bg-white p-2 rounded mt-2 overflow-x-auto">
                      {JSON.stringify(log.changes, null, 2)}
                    </pre>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
